import React, { useState, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useFormikContext, Field } from 'formik';
import { Input, Tooltip } from 'syngenta-digital-cropwise-react-ui-kit';
import InfoIcon from 'assets/info.svg?react';
import { DEFAULT_ROUND, Maths } from 'helpers/maths';
import formatNumber from 'helpers/formatNumber';
import Decimal from 'decimal.js';

function debounce(func, wait) {
  let timeout;
  // eslint-disable-next-line func-names
  return function(...args) {
    const context = this;
    clearTimeout(timeout);
    timeout = setTimeout(() => func.apply(context, args), wait);
  };
}

export const DEFAULT_BLUR_TIMEOUT = 300;

const FormInputWithPrecisionDisplay = ({
  object,
  fieldName,
  type,
  isRequired,
  precision,
  maxPrecision,
  inputSuffix,
  id,
  inputPrefix,
  onControlledChange,
  disabled,
  round,
  onControlledBlur,
  label,
  tooltip,
  tooltipPlacement,
  tooltipAlign,
  className,
  addonAfter,
  status,
  allowEmptyDisplay = false,
  allowNegative = false
}) => {
  let {
    values,
    touched,
    errors,
    setFieldValue,
    setFieldTouched,
    validateField
  } = { values: [], touched: [], errors: [] };
  const formikContext = useFormikContext();
  if (!object && formikContext) {
    values = formikContext.values;
    touched = formikContext.touched;
    errors = formikContext.errors;
    setFieldValue = formikContext.setFieldValue;
    setFieldTouched = formikContext.setFieldTouched;
    validateField = formikContext.validateField;
  }

  const error = errors[fieldName];
  const [fieldValueOnFocus, setFieldValueOnFocus] = useState(0);
  const [displayValue, setDisplayValue] = useState('');
  const [isEditing, setIsEditing] = useState(false);

  function formatCurrency(num) {
    const regex = /(\d)(?=(\d{3})+(?!\d))/g;
    const numStr = num?.toString() ?? '';

    // Handle negative numbers
    const isNegative = numStr.startsWith('-');
    const absoluteValue = isNegative ? numStr.substring(1) : numStr;

    let formatted;
    if (absoluteValue.includes('.')) {
      const [integerPart, decimalPart] = absoluteValue.split('.');
      const integerPartWithCommas = integerPart.replaceAll(regex, '$1,');
      formatted = `${integerPartWithCommas}.${decimalPart ?? ''}`;
    } else {
      formatted = absoluteValue.replaceAll(regex, '$1,');
    }

    return isNegative ? `-${formatted}` : formatted;
  }

  const sanitizeValue = useCallback(
    value =>
      value?.toString()?.replaceAll(allowNegative ? /[^\d.-]/g : /[^\d.]/g, ''),
    [allowNegative]
  );

  const formatWithPrecision = useCallback(
    (value, precisionValue) => {
      const roundedValue = Maths.parseFloatRound(
        sanitizeValue(value),
        precisionValue,
        Decimal.ROUND_HALF_UP
      )?.toFixed(precisionValue);
      const isNonValidValue = Number.isNaN(roundedValue);
      const valueRounded = isNonValidValue ? value : roundedValue;
      return formatNumber(
        valueRounded,
        '',
        precisionValue,
        Decimal.ROUND_HALF_UP
      );
    },
    [sanitizeValue]
  );

  const formatWithMaxPrecision = useCallback(
    (value, maxPrecisionValue) => {
      const roundedValue = Maths.parseFloatRound(
        sanitizeValue(value),
        maxPrecisionValue,
        Decimal.ROUND_HALF_UP
      );
      const isNonValidValue = Number.isNaN(roundedValue);
      const valueRounded = isNonValidValue ? value : roundedValue;

      if (valueRounded == null || valueRounded === '') return '';

      const numValue = Number.parseFloat(valueRounded);
      if (Number.isNaN(numValue)) return valueRounded;

      const formatter = new Intl.NumberFormat('en-US', {
        minimumFractionDigits: 0,
        maximumFractionDigits: maxPrecisionValue
      });
      return formatter.format(numValue);
    },
    [sanitizeValue]
  );

  const updateDisplayValue = useCallback(
    value => {
      if (error) {
        setDisplayValue(value);
        return;
      }

      if ((value == null || value === '') && allowEmptyDisplay) {
        setDisplayValue('');
        return;
      }

      const formattedValue =
        maxPrecision === undefined
          ? formatWithPrecision(value, precision)
          : formatWithMaxPrecision(value, maxPrecision);

      setDisplayValue(formattedValue);
    },
    [
      precision,
      maxPrecision,
      error,
      allowEmptyDisplay,
      formatWithPrecision,
      formatWithMaxPrecision
    ]
  );

  useEffect(() => {
    const value = object ? object[fieldName] : values[fieldName];
    setFieldValueOnFocus(formatCurrency(value));
  }, [object, values, fieldName, updateDisplayValue, isEditing]);

  useEffect(() => {
    if (!isEditing) {
      updateDisplayValue(fieldValueOnFocus);
    }
  }, [isEditing, fieldValueOnFocus, updateDisplayValue]);

  const handleChange = field => event => {
    const { value, selectionStart } = event.target;
    const element = event.target;
    const sanitizedValue = allowNegative
      ? value
          .replaceAll(/[^0-9.-]/g, '') // Allow digits, dots, and minus sign
          .replaceAll(/(?!^)-/g, '') // Remove minus signs that are not at the start
          .replaceAll(/\.(?=.*\.)/g, '') // Remove duplicate dots
      : value
          .replaceAll(/[^0-9.]/g, '') // Only allow digits and dots
          .replaceAll(/\.(?=.*\.)/g, ''); // Remove duplicate dots
    if (object) {
      // eslint-disable-next-line no-param-reassign
      object[field] = sanitizedValue;
      updateDisplayValue(sanitizedValue);
    } else {
      setFieldTouched(fieldName, true);
      setFieldValue(field, sanitizedValue).then(() => {
        validateField(field);
        updateDisplayValue(sanitizedValue);
      });
    }
    const formattedValue = formatCurrency(sanitizedValue);

    setFieldValueOnFocus(formattedValue);
    window.requestAnimationFrame(() => {
      // compare the formatted value with the value in the input in amount of commas
      // if the formatted value has more commas than the value in the input
      // it means that the user has typed a comma
      // and we should move the cursor to the right
      // if it is equal, it means that we need to keep the cursor in the same position
      // otherwise, we should move the cursor to the left
      const commasInFormattedValue = (formattedValue.match(/,/g) || []).length;
      const commasInValue = (value.match(/,/g) || []).length;
      let newSelectionStart;
      if (commasInFormattedValue > commasInValue) {
        newSelectionStart = selectionStart + 1;
      } else if (commasInFormattedValue < commasInValue) {
        newSelectionStart = selectionStart - 1;
      } else {
        newSelectionStart = selectionStart;
      }
      element.selectionStart = newSelectionStart;
      element.selectionEnd = newSelectionStart;
    });

    if (onControlledChange) onControlledChange(event);
  };

  const handleBlur = debounce(() => {
    setIsEditing(false);
    if (!errors[fieldName]) {
      const updatedValue = Maths.parseFloatRound(
        fieldValueOnFocus
          ?.toString()
          ?.replaceAll(allowNegative ? /[^\d.-]/g : /[^\d.]/g, ''),
        round,
        Decimal.ROUND_HALF_UP
      );
      const updatedValueSanitized = isNaN(updatedValue) ? '' : updatedValue;
      if (object) {
        // eslint-disable-next-line no-param-reassign
        object[fieldName] = updatedValueSanitized;
        updateDisplayValue(fieldValueOnFocus);
      } else {
        setFieldValue(fieldName, updatedValueSanitized).then(() => {
          validateField(fieldName);
          updateDisplayValue(fieldValueOnFocus);
        });
        // if there are errors with the field,
        // we should not set it as untouched,
        // otherswise the error message
        // will not be displayed
        setFieldTouched(fieldName, false);
      }
      if (onControlledBlur) onControlledBlur(updatedValueSanitized);
    }
    if (onControlledBlur) onControlledBlur(fieldValueOnFocus);
  }, DEFAULT_BLUR_TIMEOUT);

  const handleFocus = () => {
    setIsEditing(true);
  };

  const innerInput = (
    <div className="relative w-full">
      {tooltip && (
        <div className="syt-antd-input-label">
          <label>
            <span className="inline-flex items-center">
              {label}
              <Tooltip
                title={tooltip}
                placement={tooltipPlacement}
                color="#232630"
                autoAdjustOverflow={false}
                align={tooltipAlign}
              >
                <span
                  className="inline-flex items-center cursor-help ml-1"
                  style={{ width: 16, height: 16, flexShrink: 0 }}
                >
                  <InfoIcon className="w-16px h-16px" />
                </span>
              </Tooltip>
            </span>
          </label>
        </div>
      )}
      <Input
        data-testid={`${fieldName}-input`}
        id={id}
        disabled={disabled}
        prefix={inputPrefix}
        suffix={inputSuffix}
        size="middle"
        type={type}
        required={isRequired}
        status={status}
        className={`${className} ${addonAfter ? 'cwf-grouped-input' : ''} ${
          errors[fieldName] && touched[fieldName] ? 'border border-remove' : ''
        }`}
        value={isEditing ? fieldValueOnFocus : displayValue}
        onChange={handleChange(fieldName)}
        onBlur={handleBlur}
        onFocus={handleFocus}
        label={tooltip ? '' : label}
        addonAfter={addonAfter}
      />
    </div>
  );

  return object ? (
    innerInput
  ) : (
    <Field name={fieldName}>{() => innerInput}</Field>
  );
};

FormInputWithPrecisionDisplay.propTypes = {
  fieldName: PropTypes.string.isRequired,
  isRequired: PropTypes.bool,
  precision: PropTypes.number,
  maxPrecision: PropTypes.number,
  inputSuffix: PropTypes.node,
  inputPrefix: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  id: PropTypes.string,
  type: PropTypes.string,
  onControlledChange: PropTypes.func,
  round: PropTypes.number,
  // eslint-disable-next-line react/forbid-prop-types
  object: PropTypes.object,
  disabled: PropTypes.bool,
  onControlledBlur: PropTypes.func,
  label: PropTypes.node,
  tooltip: PropTypes.string,
  tooltipPlacement: PropTypes.string,
  // eslint-disable-next-line react/forbid-prop-types
  tooltipAlign: PropTypes.object,
  className: PropTypes.string,
  addonAfter: PropTypes.node,
  status: PropTypes.string,
  allowEmptyDisplay: PropTypes.bool,
  allowNegative: PropTypes.bool
};

FormInputWithPrecisionDisplay.defaultProps = {
  object: null,
  isRequired: false,
  precision: 2,
  allowNegative: false,
  maxPrecision: undefined,
  inputSuffix: null,
  inputPrefix: '',
  id: '',
  type: 'text',
  onControlledChange: null,
  round: DEFAULT_ROUND,
  disabled: false,
  onControlledBlur: null,
  label: '',
  tooltip: '',
  tooltipPlacement: 'top',
  tooltipAlign: undefined,
  className: '',
  addonAfter: null,
  status: '',
  allowEmptyDisplay: false
};

export default FormInputWithPrecisionDisplay;
